import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

/* ─── helpers ─── */

/** Read a CSS custom property's computed value from <html>. */
function css(prop: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
  return v || fallback;
}

/** Parse "0.375rem" / "8px" → number for MUI shape.borderRadius. */
function pxOrRem(raw: string, fallback: number): number {
  if (raw.endsWith('rem')) {
    const n = parseFloat(raw);
    return isNaN(n) ? fallback : Math.round(n * 16);
  }
  const n = parseFloat(raw);
  return isNaN(n) ? fallback : Math.round(n);
}

/**
 * Build MUI theme from the frontend's CSS custom properties.
 * Called on mount and whenever <html> classes change (theme switch).
 */
function buildThemeFromCSS(): { theme: Theme; isDark: boolean } {
  const isDark = document.documentElement.classList.contains('dark');

  // ── colours ──
  const primary       = css('--color-primary',            isDark ? '#90caf9' : '#1976d2');
  const primaryHover  = css('--color-primary-hover',      isDark ? '#42a5f5' : '#1565c0');
  const secondary     = css('--color-secondary',          isDark ? '#f48fb1' : '#dc004e');
  const secondaryHover= css('--color-secondary-hover',    isDark ? '#3b82f6' : '#60a5fa');
  const bgBody        = css('--color-background-body',    isDark ? '#121212' : '#f5f5f5');
  const bgSurface     = css('--color-background-surface', isDark ? '#1e1e1e' : '#ffffff');
  const bgInput       = css('--color-background-input',   isDark ? '#121212' : '#f1f5f9');
  const textPrimary   = css('--color-text-primary',       isDark ? '#ffffff' : '#212121');
  const textSecondary = css('--color-text-secondary',     isDark ? 'rgba(255,255,255,0.7)' : '#757575');
  const error         = css('--color-destructive',        '#ef4444');
  const success       = css('--color-success',            '#22c55e');
  const warning       = css('--color-warning',            '#f59e0b');
  const info          = css('--color-info',               '#3b82f6');
  const divider       = css('--color-border-default',     isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)');

  // ── typography & shape ──
  const fontFamily   = css('--font-primary', '"Roboto","Helvetica","Arial",sans-serif');
  const borderRadius = pxOrRem(css('--radius-md', '6'), 6);
  const textTransform = css('--text-transform-btn', 'none') as 'none' | 'uppercase';

  const theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary:    { main: primary, dark: primaryHover },
      secondary:  { main: secondary, dark: secondaryHover },
      background: { default: bgBody, paper: bgSurface },
      text:       { primary: textPrimary, secondary: textSecondary },
      error:      { main: error },
      success:    { main: success },
      warning:    { main: warning },
      info:       { main: info },
      divider,
    },
    typography: { fontFamily },
    shape:      { borderRadius },
    components: {
      MuiButton:  { styleOverrides: { root: { textTransform } } },
      MuiPaper:   { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiAppBar:  { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiDrawer:  { styleOverrides: { paper: { backgroundImage: 'none' } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: bgInput },
        },
      },
    },
  });

  return { theme, isDark };
}

/* ─── provider ─── */

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeState, setThemeState] = useState(buildThemeFromCSS);

  /* Re-build MUI theme whenever <html> classes change (main app theme switch) */
  useEffect(() => {
    const rebuild = () => setThemeState(buildThemeFromCSS());

    const observer = new MutationObserver(rebuild);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  /* toggleTheme kept for backward-compat — delegates to main app */
  const toggleTheme = useCallback(() => {
    document.documentElement.classList.toggle('dark');
  }, []);

  const contextValue = useMemo(
    () => ({
      mode: (themeState.isDark ? 'dark' : 'light') as ThemeMode,
      toggleTheme,
      isDark: themeState.isDark,
    }),
    [themeState.isDark, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={themeState.theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
